import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import { ChevronRight, CheckCircle2, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import axios from "axios";

// Layout & UI
import AuthLayout from "../../Auth/AuthLayout";
import GlassCard from "../../Auth/GlassCard";
import AuthHeader from "../../Auth/AuthHeader";

// Steps
import {
  StepProfile,
  StepOtp,
  StepDevice,
  StepPlan,
} from "../customersignup/StepRenderer";

import PincodeObserver from "../customersignup/PincodeObserver";
import { validationSchemas } from "../../../validations/SignupSchema";
import { BASE_URL } from "../../config/api";
import SuccessScreen from "../customersignup/SuccessScreen";

const steps = ["Profile", "OTP", "Device", "Plan"];

export default function CustomerSignup() {
  const [activeStep, setActiveStep] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  const otpRefs = useRef([]);
  const navigate = useNavigate();

  const isLastStep = activeStep === steps.length - 1;

  // ================= SEND OTP FUNCTION =================
  const sendOtp = async (mobile) => {
    try {
      await axios.post(
        "https://uatwarranty.goelectronix.co.in/api/warranty/send-customer-wa-otp",
        {
          mobile,
          company_id: 3,
        },
      );
      toast.success("Verification code sent to WhatsApp");
      return true;
    } catch (error) {
      toast.error("Failed to send OTP. Please check the number.");
      return false;
    }
  };

  // ================= CREATE CUSTOMER (STEP 0) =================
  const handleProfileSubmit = async (values) => {
    try {
      if(localStorage.getItem("customer_data")) localStorage.removeItem("customer_data");
      const payload = {
        name: values.fullName,
        mobile: values.mobile,
        email: values.email,
        state: values.state,
        city: values.district,
        pincode: values.pincode,
        retailer_id: 4,
        company_id: 3,
      };

      const response = await axios.post(
        `${BASE_URL}warranty/create-customer-new`,
        payload,
      );


      if (
        response.status === 200 &&
        (response.data.success || response.data.data)
      ) {
        const customerData = response.data.data || response.data;
        localStorage.setItem("customer_data", JSON.stringify(customerData));
        toast.success("Profile created successfully!");
        return true;
      } else {
        toast.error(response.data.message || "Failed to create profile");
        return false;
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Server error occurred");
      return false;
    }
  };

  // ================= MAIN STEP HANDLER =================
  const handleStepSubmit = async (values, actions) => {
    const { setSubmitting } = actions;

    // --------- STEP 0: PROFILE ---------
    if (activeStep === 0) {
      const success = await handleProfileSubmit(values);
      if (!success) {
        setSubmitting(false);
        return;
      }

      const otpSent = await sendOtp(values.mobile);
      if (!otpSent) {
        setSubmitting(false);
        return;
      }

      setActiveStep(1);
      setSubmitting(false);
      return;
    }

    // --------- STEP 1: OTP ---------
    if (activeStep === 1) {
      if (!isVerified) {
        toast.error("Please verify your mobile number first");
        setSubmitting(false);
        return;
      }
      setActiveStep(2);
      setSubmitting(false);
      return;
    }

    // --------- FINAL STEP: SUBMISSION ---------
    if (isLastStep) {
      try {
        debugger;
        setSubmitting(true);
        const storedCustomer = JSON.parse(
          localStorage.getItem("customer_data"),
        );

        // Get IDs from stored customer or values
        const customerId = storedCustomer?.id || storedCustomer?.data?.id;
        const today = new Date();

        // 2. Add validity days (e.g., 183) to today
        // We parse the value to a Number just in case it's a string
        const validityDays = Number(values.validity_days || 0);
        const expiryDateObj = new Date();
        expiryDateObj.setDate(today.getDate() + validityDays);

        // 3. Format to DD-MM-YYYY
        const day = String(expiryDateObj.getDate()).padStart(2, "0");
        const month = String(expiryDateObj.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
        const year = expiryDateObj.getFullYear();

        const formattedExpiryDate = `${day}-${month}-${year}`;
        // Construct the full payload exactly as requested
        const finalPayload = {
          // Device Identifiers
          imei1: values.imei,
          imei2: values.imei2 || "", // Added imei2
          serial: values.serial,

          // Product/Plan Info
          product_id: Number(values.productId),
          product_name: values.productName || "",
          product_price: Number(values.planPrice),
          product_mrp: Number(values.productMrp || 0),

          // Entities
          company_id: 3, // Matches your create-customer company_id
          retailer_id: 4, // Matches your create-customer retailer_id
          w_customer_id: customerId,

          // Brand & Category Details
          brand_id: Number(values.brandId),
          brand_name: values.brand,
          category_id: Number(values.categoryId),
          category_name: values.categoryName || "",
          model_id: Number(values.modelId),
          model: values.deviceModel,

          // Additional Info
          available_claim: 1,
          expiry_date: formattedExpiryDate || "", // Calculate this if needed
          name: values.fullName,
          device_price: Number(values.price),

          // Documents/Links
          document_url: values.documentUrl || "",
          link1: values.link1 || "",
          link2: values.link2 || "",
        };

        const response = await axios.post(
          `${BASE_URL}warranty/create-warranty-by-customer`,
          finalPayload,
        );

        if (response.data.status !== false) {
          toast.success("Registration Completed!");
          localStorage.removeItem("customer_data");
          setSubmittedData(values);
          setShowSuccess(true);
        } else {
          toast.error(response.data.message || "Failed to create warranty");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Final submission failed",
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // --------- DEFAULT NEXT ---------
    setActiveStep((s) => s + 1);
    setSubmitting(false);
  };

  // ================= AUTO RESUME =================
  useEffect(() => {
    const customer = localStorage.getItem("customer_data");
    if (customer) {
      // If profile exists, start at Step 1 (OTP/Mobile)
      setActiveStep(1);
    }
  }, []);

  return (
    <AuthLayout>
      {showSuccess && (
        <SuccessScreen
          data={submittedData}
          onOk={() => {
            navigate("/cust");
          }}
        />
      )}

      <div className="max-w-2xl mx-auto py-8">
        <GlassCard>
          <AuthHeader
            title="Customer SignUp"
            subtitle={`Step ${activeStep + 1} of ${steps.length}`}
          />

          {/* PROGRESS STEPPER */}
          <div className="flex justify-between items-center mb-12 px-2 relative">
            <div className="absolute top-5 left-0 w-full h-[2px] bg-slate-100 -z-10" />
            {steps.map((step, idx) => (
              <div key={idx} className="flex flex-col items-center group">
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-2xl font-bold transition-all duration-300 border-4 ${
                    activeStep >= idx
                      ? "bg-emerald-600 border-emerald-100 text-white shadow-lg shadow-emerald-200"
                      : "bg-white border-slate-50 text-slate-400"
                  }`}
                >
                  {activeStep > idx ? <CheckCircle2 size={20} /> : idx + 1}
                </div>
                <span
                  className={`text-[10px] font-bold mt-3 uppercase tracking-widest ${
                    activeStep >= idx ? "text-emerald-700" : "text-slate-400"
                  }`}
                >
                  {step}
                </span>
              </div>
            ))}
          </div>

          <Formik
            initialValues={{
              fullName: "",
              mobile:
                JSON.parse(localStorage.getItem("customer_data"))?.mobile || "",
              otp: "",
              email: "",
              pincode: "",
              district: "",
              state: "",
              deviceModel: "",
              brand: "",
              price: "",
              serial: "",
              imei: "",
              productId: "",
              planPrice: 0,
            }}
            validationSchema={validationSchemas[activeStep]}
            onSubmit={handleStepSubmit}
            enableReinitialize={false}
          >
            {(formikProps) => (
              <Form className="space-y-4">
                <PincodeObserver setFieldValue={formikProps.setFieldValue} />

                <div className="min-h-[300px]">
                  {activeStep === 0 && <StepProfile {...formikProps} />}

                  {activeStep === 1 && (
                    <StepOtp
                      {...formikProps}
                      otpRefs={otpRefs}
                      isVerified={isVerified}
                      setIsVerified={setIsVerified}
                      setActiveStep={setActiveStep}
                      sendOtp={sendOtp} // ✅ PASSING FUNCTION TO COMPONENT
                    />
                  )}

                  {activeStep === 2 && <StepDevice {...formikProps} />}
                  {activeStep === 3 && <StepPlan {...formikProps} />}
                </div>

                {/* NAVIGATION BUTTONS */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={
                      formikProps.isSubmitting ||
                      (activeStep === 1 && !isVerified)
                    }
                    className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-[1.5rem] font-bold shadow-xl shadow-emerald-100 transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:grayscale"
                  >
                    {formikProps.isSubmitting ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      <>
                        {activeStep === 0
                          ? "Create Profile"
                          : activeStep === 1
                            ? "Proceed to Device"
                            : isLastStep
                              ? "Complete Registration"
                              : "Continue"}
                        <ChevronRight size={20} />
                      </>
                    )}
                  </button>

                  {activeStep > 1 && !isVerified && (
                    <button
                      type="button"
                      onClick={() => setActiveStep((s) => s - 1)}
                      className="w-full mt-4 py-2 text-slate-400 text-sm font-bold hover:text-slate-600 transition-colors"
                    >
                      Go Back
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </GlassCard>
      </div>
    </AuthLayout>
  );
}

// import React, { useState, useRef, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { Formik, Form } from "formik";
// import { ChevronRight, CheckCircle2 } from "lucide-react";
// import toast from "react-hot-toast";
// import axios from "axios";

// // Layout & UI
// import AuthLayout from "../../Auth/AuthLayout";
// import GlassCard from "../../Auth/GlassCard";
// import AuthHeader from "../../Auth/AuthHeader";

// // Local Sub-components
// import {
//   StepProfile,
//   StepDevice,
//   StepPlan,
// } from "../customersignup/StepRenderer";
// import PincodeObserver from "../customersignup/PincodeObserver";
// import { validationSchemas } from "../../../validations/SignupSchema";
// import { BASE_URL } from "../../config/api";
// import SuccessScreen from "../customersignup/SuccessScreen";

// const steps = ["Profile", "OTP", "Device", "Plan"];

// export default function CustomerSignup() {
//   const [activeStep, setActiveStep] = useState(0);
//   const [isVerified, setIsVerified] = useState(false);
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [submittedData, setSubmittedData] = useState(null);
//   const otpRefs = useRef([]);
//   const navigate = useNavigate();

//   const isLastStep = activeStep === steps.length - 1;

//   /**
//    * API: Create Customer Profile (Step 0)
//    */
//   const handleProfileSubmit = async (values) => {
//     try {
//       const payload = {
//         name: values.fullName,
//         mobile: values.mobile,
//         email: values.email,
//         state: values.state,
//         city: values.district,
//         pincode: values.pincode,
//         retailer_id: 4,
//         company_id: 3,
//       };

//       const response = await axios.post(
//         `${BASE_URL}warranty/create-customer-new`,
//         payload,
//       );

//       console.log("API Response:", response.data);

//       if (
//         response.status === 200 &&
//         (response.data.success || response.data.data)
//       ) {
//         const customerData = response.data.data || response.data;
//         localStorage.setItem("customer_data", JSON.stringify(customerData));
//         toast.success("Profile saved!");
//         return true;
//       } else {
//         toast.error(response.data.message || "Failed to create profile");
//         return false;
//       }
//     } catch (error) {
//       console.error("API Error:", error.response?.data);
//       toast.error(error?.response?.data?.message || "Server error");
//       return false;
//     }
//   };

//   /**
//    * Main submission logic handler
//    */
//   /**
//    * Main submission logic handler
//    */
//   const handleStepSubmit = async (values, actions) => {
//     const { setSubmitting, setErrors, setTouched } = actions;

//     // --- STEP 0: PROFILE & OTP ---
//     if (activeStep === 0) {
//       if (!isVerified) {
//         toast.error("Please verify your mobile number first!");
//         setSubmitting(false);
//         return;
//       }

//       const apiSuccess = await handleProfileSubmit(values);
//       if (!apiSuccess) {
//         setSubmitting(false);
//         return;
//       }
//       setErrors({});
//     }

//     // --- FINAL STEP: COMPLETE ---
//     if (isLastStep) {
//       try {
//         setSubmitting(true);

//         // 1. Get stored customer data
//         const storedCustomer = JSON.parse(
//           localStorage.getItem("customer_data"),
//         );
//         const customerId = storedCustomer?.id || storedCustomer?.data?.id;

//         // 2. Calculate Expiry Date based on validity_days from selected plan
//         // Default to 365 if not found, but StepPlan should set this value
//         const daysToAdd = Number(values.validity_days) || 0;

//         const today = new Date();
//         const expiryDateObj = new Date(today);

//         expiryDateObj.setDate(today.getDate() + daysToAdd);

//         // ✅ FORMAT: DD-MM-YY
//         const day = String(expiryDateObj.getDate()).padStart(2, "0");
//         const month = String(expiryDateObj.getMonth() + 1).padStart(2, "0");
//         const year = String(expiryDateObj.getFullYear()).slice(-2);

//         const formattedExpiryDate = `${day}-${month}-${year}`;

//         // 3. Build final payload
//         const finalPayload = {
//           imei1: values.imei,
//           imei2: values.imei2 || "",
//           serial: values.serial || "",
//           product_id: values.productId,
//           company_id: 3,
//           retailer_id: 4,
//           w_customer_id: customerId,
//           brand_id: values.brandId,
//           category_id: values.categoryId,
//           model_id: values.modelId,
//           product_name: values.package,
//           brand_name: values.brand,
//           model: values.deviceModel,
//           category_name: values.categoryName || "Smartphones",
//           available_claim: values.claims || 1, // From JSON data
//           expiry_date: formattedExpiryDate, // Dynamically calculated
//           document_url: "https://example.com/invoice.pdf", // Replace with actual file upload URL if available
//           device_price: Number(values.price),
//           product_price: Number(values.planPrice),
//           product_mrp: Number(values.planPrice),
//           name: values.fullName,
//         };

//         const response = await axios.post(
//           `${BASE_URL}warranty/create-warranty-by-customer`,
//           finalPayload,
//         );

//         if (response.data.status !== false) {
//           toast.success("Registration Complete!");
//           setSubmittedData(values);
//           setShowSuccess(true);
//         } else {
//           toast.error(response.data.message || "Failed to create warranty.");
//         }
//       } catch (error) {
//         console.error("Final Submission Error:", error.response?.data);
//         toast.error(error.response?.data?.message || "Submission failed.");
//       } finally {
//         setSubmitting(false);
//       }
//     }
//     // --- NEXT STEP ---
//     else {
//       setActiveStep((s) => s + 1);
//       setTouched({});
//       setSubmitting(false);
//     }
//   };

//   useEffect(() => {
//     const customer = localStorage.getItem("customer_data");
//     if (customer) {
//       setActiveStep(1);
//       setIsVerified(true);
//     }
//   }, []);

//   return (
//     <AuthLayout>
//       {showSuccess && (
//         <SuccessScreen data={submittedData} onOk={() =>{ navigate("/cust");localStorage.removeItem("customer_data")}} />
//       )}
//       <div className="w-full mx-auto px-2 sm:px-4 py-4 md:py-8">
//         <GlassCard className="!p-5 sm:!p-8 md:!p-10 !rounded-[2rem] border-white/40 shadow-md">
//           <AuthHeader
//             title="Customer SignUp"
//             subtitle="Protect your hardware in minutes"
//           />

//           {/* Stepper UI */}
//           <div className="flex items-center justify-between mb-12 relative px-4 max-w-sm mx-auto">
//             {steps.map((step, idx) => (
//               <div key={idx} className="z-10 flex flex-col items-center">
//                 <div
//                   className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500 border-2 ${
//                     activeStep === idx
//                       ? "bg-emerald-600 border-emerald-600 text-white shadow-lg"
//                       : activeStep > idx
//                         ? "bg-emerald-50 border-emerald-100 text-emerald-600"
//                         : "bg-white border-slate-100 text-slate-300"
//                   }`}
//                 >
//                   {activeStep > idx ? (
//                     <CheckCircle2 size={16} />
//                   ) : (
//                     <span className="text-xs font-black">{idx + 1}</span>
//                   )}
//                 </div>
//                 <span
//                   className={`mt-2 text-[9px] font-black uppercase tracking-wider ${activeStep === idx ? "text-emerald-700" : "text-slate-300"}`}
//                 >
//                   {step}
//                 </span>
//               </div>
//             ))}
//             <div className="absolute top-5 left-0 w-full h-[2px] bg-slate-100 -z-0" />
//           </div>

//           <Formik
//             initialValues={{
//               // Step 0 Profile
//               fullName: "",
//               mobile: "",
//               otp: "",
//               email: "",
//               pincode: "",
//               district: "",
//               state: "",
//               // Step 1 Device
//               deviceModel: "",
//               brand: "",
//               price: "",
//               serial: "",
//               imei: "",
//               imei2: "",
//               brandId: "",
//               categoryId: "",
//               modelId: "",
//               categoryName: "",
//               // Step 2 Plan
//               package: "",
//               planPrice: "",
//               productId: "",
//             }}
//             validationSchema={validationSchemas[activeStep]}
//             onSubmit={handleStepSubmit}
//           >
//             {(formikProps) => (
//               <Form className="w-full">
//                 <PincodeObserver setFieldValue={formikProps.setFieldValue} />

//                 <div className="min-h-[300px]">
//                   {activeStep === 0 && (
//                     <StepProfile
//                       {...formikProps}
//                       otpRefs={otpRefs}
//                       isVerified={isVerified}
//                       setIsVerified={setIsVerified}
//                     />
//                   )}

//                   {activeStep === 1 && <StepDevice {...formikProps} />}

//                   {activeStep === 2 && <StepPlan {...formikProps} />}
//                 </div>

//                 <div className="flex flex-col-reverse sm:flex-row gap-3 pt-8">
//                   {activeStep === 1 &&
//                     !localStorage.getItem("customer_data") && (
//                       <button
//                         type="button"
//                         disabled={formikProps.isSubmitting}
//                         onClick={() => setActiveStep((s) => s - 1)}
//                         className="w-full py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-30"
//                       >
//                         Back
//                       </button>
//                     )}
//                   {activeStep === 2 && (
//                     <button
//                       type="button"
//                       disabled={formikProps.isSubmitting}
//                       onClick={() => setActiveStep((s) => s - 1)}
//                       className="w-full py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-30"
//                     >
//                       Back
//                     </button>
//                   )}

//                   <button
//                     type="submit"
//                     disabled={
//                       formikProps.isSubmitting ||
//                       (activeStep === 2 && !formikProps.values.productId)
//                     }
//                     className="w-full flex items-center justify-center gap-2 rounded-xl py-4 text-[10px] font-black uppercase tracking-widest text-white transition-all active:scale-95 shadow-md shadow-emerald-200 disabled:opacity-50"
//                     style={{
//                       background:
//                         "linear-gradient(135deg, #059669 0%, #10b981 100%)",
//                     }}
//                   >
//                     {formikProps.isSubmitting
//                       ? "Processing..."
//                       : activeStep === 0
//                         ? "Verify & Continue"
//                         : isLastStep
//                           ? "Complete"
//                           : "Next"}
//                     {!formikProps.isSubmitting && <ChevronRight size={14} />}
//                   </button>
//                 </div>
//               </Form>
//             )}
//           </Formik>
//         </GlassCard>
//       </div>
//     </AuthLayout>
//   );
// }

// import React, { useState, useEffect, useRef } from "react";
// import { useNavigate } from "react-router-dom";
// import { Formik, Form, useFormikContext } from "formik";
// import * as Yup from "yup";
// import Select from "react-select";
// import {
//   User, Phone, Mail, MapPin, Smartphone,
//   Hash, CheckCircle2, ChevronRight, ShieldCheck, Sparkles, MessageSquare, Map
// } from "lucide-react";

// import AuthLayout from "../../Auth/AuthLayout";
// import GlassCard from "../../Auth/GlassCard";
// import AuthHeader from "../../Auth/AuthHeader";

// const steps = ["Profile", "Device", "Plan"];

// /**
//  * Enhanced PincodeObserver
//  * Listens for 6-digit pincode and populates District/State.
//  */
// const PincodeObserver = ({ setFieldValue }) => {
//   const { values } = useFormikContext();
//   const [loading, setLoading] = useState(false);

//   useEffect(() => {
//     if (values.pincode && values.pincode.length === 6) {
//       const fetchAddress = async () => {
//         setLoading(true);
//         try {
//           const response = await fetch(`https://goelectronix.in/api/v1/pincode/${values.pincode}`);
//           const result = await response.json();

//           if (result.success) {
//             setFieldValue("state", result.state || "");
//             setFieldValue("district", result.district || "");
//           }
//         } catch (error) {
//           console.error("Pincode API Error:", error);
//         } finally {
//           setLoading(false);
//         }
//       };
//       fetchAddress();
//     }
//   }, [values.pincode, setFieldValue]);

//   return loading ? (
//     <div className="absolute right-6 top-10 flex items-center gap-2 text-emerald-500 animate-in fade-in duration-300">
//       <span className="text-[9px] font-black uppercase tracking-tighter">Locating...</span>
//       <Sparkles size={14} className="animate-spin" />
//     </div>
//   ) : null;
// };

// export default function CustomerSignup() {
//   const [activeStep, setActiveStep] = useState(0);
//   const [packages, setPackages] = useState([]);
//   const [showOtp, setShowOtp] = useState(false);
//   const otpRefs = useRef([]);
//   const navigate = useNavigate();

//   useEffect(() => {
//     setPackages([
//       { id: 1, name: "Essential Shield", price: 999, desc: "Mechanical breakdown coverage.", feat: "1 Year" },
//       { id: 2, name: "Elite Protection", price: 1999, desc: "Accidental & liquid damage.", feat: "Full ADLD" },
//     ]);
//   }, []);

//   const deviceOptions = [
//     { value: "iPhone13", label: "iPhone 13", brand: "Apple", price: 70000 },
//     { value: "SamsungS22", label: "Samsung S22", brand: "Samsung", price: 60000 },
//     { value: "Pixel7", label: "Google Pixel 7", brand: "Google", price: 55000 },
//   ];

//   const selectStyles = {
//     menuPortal: base => ({ ...base, zIndex: 9999 }),
//     menu: base => ({ ...base, zIndex: 9999, borderRadius: '12px' }),
//     control: (base, state) => ({
//       ...base,
//       backgroundColor: state.isFocused ? "white" : "#f8fafc",
//       border: "2px solid",
//       borderColor: state.isFocused ? "#10b981" : "#f8fafc",
//       borderRadius: "0.75rem",
//       padding: "2px",
//       boxShadow: "none",
//       "&:hover": { borderColor: "#10b981" }
//     }),
//     option: (base, state) => ({
//       ...base,
//       backgroundColor: state.isSelected ? "#059669" : state.isFocused ? "#ecfdf5" : "white",
//       color: state.isSelected ? "white" : "#334155",
//       fontSize: "14px",
//       fontWeight: "600"
//     })
//   };

//   const validationSchemas = [
//     Yup.object({
//       fullName: Yup.string().required("Required"),
//       mobile: Yup.string().matches(/^[0-9]{10}$/, "10 digits required").required("Required"),
//       otp: Yup.string().when("mobile", {
//         is: (val) => val && val.length === 10,
//         then: () => Yup.string().length(6, "Enter 6 digits").required("Required"),
//       }),
//       email: Yup.string().email("Invalid email").required("Required"),
//       pincode: Yup.string().length(6, "6 digits").required("Required"),
//       district: Yup.string().required("Required"),
//       state: Yup.string().required("Required"),
//     }),
//     Yup.object({
//       deviceModel: Yup.string().required("Select a model"),
//     }),
//     Yup.object({
//       package: Yup.string().required("Select a plan"),
//     }),
//   ];

//   const CustomInput = ({ label, icon: Icon, error, touched, ...props }) => {
//     // Logic to highlight fields populated by the Pincode API
//     const isAutoFilled = (label === "District" || label === "State") && props.value && !error;

//     return (
//       <div className="mb-4 md:mb-5 group w-full">
//         <div className="flex justify-between items-center mb-1">
//           <label className="text-[9px] md:text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-1">
//             {label}
//           </label>
//           {touched && error && (
//             <span className="text-[9px] font-bold text-rose-500 animate-in fade-in">
//               {error}
//             </span>
//           )}
//         </div>
//         <div className={`relative flex items-center rounded-xl md:rounded-2xl transition-all duration-500 border-2
//           ${touched && error
//             ? "bg-rose-50/30 border-rose-100"
//             : isAutoFilled
//               ? "bg-emerald-50/40 border-emerald-200/50 shadow-sm shadow-emerald-100/20"
//               : "bg-slate-50 border-slate-50 focus-within:bg-white focus-within:border-emerald-500/30"}`}>
//           <div className={`pl-4 ${touched && error ? "text-rose-400" : isAutoFilled ? "text-emerald-500" : "text-slate-400 group-focus-within:text-emerald-500"}`}>
//             {Icon && <Icon size={16} />}
//           </div>
//           <input
//             {...props}
//             className="w-full bg-transparent px-3 py-3 md:py-4 text-sm font-semibold outline-none text-slate-700 placeholder:text-slate-300"
//           />
//           {isAutoFilled && (
//             <div className="pr-4 text-emerald-500 animate-in zoom-in duration-500">
//               <CheckCircle2 size={14} />
//             </div>
//           )}
//         </div>
//       </div>
//     );
//   };

//   return (
//     <AuthLayout>
//       <div className="w-full mx-auto px-2 sm:px-4 py-4 md:py-8">
//         <GlassCard className="!p-5 sm:!p-8 md:!p-10 !rounded-[2rem] md:!rounded-[2.5rem] border-white/40 shadow-md">
//           <AuthHeader title="Customer SignUp" subtitle="Protect your hardware in minutes" />

//           {/* Stepper */}
//           <div className="flex items-center justify-between mb-12 md:mb-16 relative px-4 max-w-sm mx-auto">
//             {steps.map((step, idx) => (
//               <div key={idx} className="z-10 flex flex-col items-center relative">
//                 <div className={`w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center transition-all duration-500 border-2 ${
//                   activeStep === idx
//                     ? "bg-emerald-600 border-emerald-600 text-white shadow-lg"
//                     : activeStep > idx ? "bg-emerald-50 border-emerald-100 text-emerald-600" : "bg-white border-slate-100 text-slate-300"
//                 }`}>
//                   {activeStep > idx ? <CheckCircle2 size={16} /> : <span className="text-xs font-black">{idx + 1}</span>}
//                 </div>
//                 <span className={`mt-2 text-[8px] md:text-[9px] font-black uppercase tracking-wider whitespace-nowrap ${activeStep === idx ? "text-emerald-700" : "text-slate-300"}`}>
//                   {step}
//                 </span>
//               </div>
//             ))}
//             <div className="absolute top-4 md:top-5 left-0 w-full h-[2px] bg-slate-100 -z-0" />
//           </div>

//           <Formik
//             initialValues={{
//               fullName: "", mobile: "", otp: "", email: "",
//               pincode: "", district: "", state: "",
//               deviceModel: "", brand: "", price: "", serial: "", imei: "", package: ""
//             }}
//             validationSchema={validationSchemas[activeStep]}
//             onSubmit={(values, actions) => {
//               if (activeStep === steps.length - 1) navigate("/cust");
//               else { setActiveStep(s => s + 1); actions.setTouched({}); }
//             }}
//           >
//             {({ values, handleChange, setFieldValue, errors, touched }) => (
//               <Form className="w-full">
//                 <div className="relative">
//                    <PincodeObserver setFieldValue={setFieldValue} />
//                 </div>

//                 {activeStep === 0 && (
//                   <div className="space-y-1 animate-in fade-in slide-in-from-bottom-2 duration-300">
//                     <CustomInput label="Name" name="fullName" icon={User} value={values.fullName} onChange={handleChange} error={errors.fullName} touched={touched.fullName} placeholder="e.g. John Doe" />

//                     <CustomInput
//                       label="Mobile"
//                       name="mobile"
//                       icon={Phone}
//                       type="tel"
//                       value={values.mobile}
//                       onChange={(e) => {
//                         const val = e.target.value.replace(/\D/g, "").slice(0, 10);
//                         setFieldValue("mobile", val);
//                         setShowOtp(val.length === 10);
//                       }}
//                       error={errors.mobile}
//                       touched={touched.mobile}
//                       placeholder="10 digits"
//                     />

//                     {showOtp && (
//                       <div className="animate-in zoom-in-95 duration-300 mb-6 p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100">
//                         <div className="flex items-center gap-2 mb-4 p-2 bg-white/60 rounded-lg border border-emerald-100">
//                           <MessageSquare size={12} className="text-emerald-600" />
//                           <p className="text-[10px] font-bold text-emerald-800">OTP has been sent to WhatsApp</p>
//                         </div>
//                         <div className="grid grid-cols-6 gap-2">
//                           {[...Array(6)].map((_, i) => (
//                             <input
//                               key={i}
//                               ref={el => otpRefs.current[i] = el}
//                               type="text"
//                               maxLength="1"
//                               className="w-full h-10 md:h-12 text-center text-base font-black bg-white border border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-0 outline-none"
//                               onChange={(e) => {
//                                 const val = e.target.value;
//                                 let otpArray = values.otp.split("");
//                                 otpArray[i] = val;
//                                 setFieldValue("otp", otpArray.join(""));
//                                 if (val && i < 5) otpRefs.current[i + 1].focus();
//                               }}
//                             />
//                           ))}
//                         </div>
//                       </div>
//                     )}

//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
//                       <CustomInput label="Email" name="email" icon={Mail} value={values.email} onChange={handleChange} error={errors.email} touched={touched.email} placeholder="name@email.com" />

//                       <CustomInput
//                          label="Pincode"
//                          name="pincode"
//                          icon={MapPin}
//                          value={values.pincode}
//                          onChange={(e) => {
//                            const val = e.target.value.replace(/\D/g, "").slice(0, 6);
//                            setFieldValue("pincode", val);
//                          }}
//                          error={errors.pincode}
//                          touched={touched.pincode}
//                          placeholder="6 digits"
//                       />
//                     </div>

//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
//                       <CustomInput
//                         label="District"
//                         name="district"
//                         icon={Map}
//                         value={values.district}
//                         onChange={handleChange}
//                         error={errors.district}
//                         touched={touched.district}
//                         placeholder="District"
//                       />
//                       <CustomInput
//                         label="State"
//                         name="state"
//                         icon={ShieldCheck}
//                         value={values.state}
//                         onChange={handleChange}
//                         error={errors.state}
//                         touched={touched.state}
//                         placeholder="State"
//                       />
//                     </div>
//                   </div>
//                 )}

//                 {activeStep === 1 && (
//                   <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
//                     <div className="mb-6">
//                       <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Search Device</label>
//                       <div className="mt-1.5 relative">
//                         <Select
//                           options={deviceOptions}
//                           styles={selectStyles}
//                           placeholder="Search e.g. iPhone..."
//                           isSearchable
//                           menuPortalTarget={document.body}
//                           onChange={(option) => {
//                             setFieldValue("deviceModel", option.label);
//                             setFieldValue("brand", option.brand);
//                             setFieldValue("price", option.price);
//                           }}
//                         />
//                       </div>
//                     </div>
//                     {values.deviceModel && (
//                        <div className="flex justify-between items-center mb-6 p-5 rounded-[1.5rem] bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-100 shadow-sm relative overflow-hidden">
//                         <div className="z-10">
//                          <p className="text-[9px] font-bold uppercase text-emerald-600 tracking-widest">{values.brand}</p>
//                          <p className="text-lg font-black text-slate-700">{values.deviceModel}</p>
//                         </div>
//                         <div className="z-10 text-right">
//                             <p className="text-lg font-black text-emerald-600">₹{values.price}</p>
//                         </div>
//                         <Sparkles className="absolute -right-4 -bottom-4 opacity-10 text-emerald-500" size={80} />
//                       </div>
//                     )}
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
//                       <CustomInput label="Serial Number" name="serial" icon={Hash} value={values.serial} onChange={handleChange} placeholder="Serial (Optional)" />
//                       <CustomInput label="IMEI Number" name="imei" icon={Smartphone} value={values.imei} onChange={handleChange} placeholder="IMEI (Optional)" />
//                     </div>
//                   </div>
//                 )}

//                 {activeStep === 2 && (
//                   <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
//                     {packages.map((pkg) => (
//                       <div
//                         key={pkg.id}
//                         onClick={() => setFieldValue("package", pkg.id)}
//                         className={`cursor-pointer p-4 rounded-2xl border-2 transition-all ${
//                           values.package === pkg.id ? "border-emerald-500 bg-emerald-50/40" : "border-slate-50 bg-slate-50/50"
//                         }`}
//                       >
//                         <div className="flex justify-between items-center gap-3">
//                            <div className="flex items-center gap-3">
//                               <div className={`p-2 rounded-lg ${values.package === pkg.id ? "bg-emerald-600 text-white" : "bg-white text-slate-400 border border-slate-100"}`}>
//                                 <ShieldCheck size={20} />
//                               </div>
//                               <div>
//                                 <p className="font-black text-xs text-slate-800">{pkg.name}</p>
//                                 <p className="text-[10px] text-slate-500">{pkg.feat}</p>
//                               </div>
//                            </div>
//                            <p className="text-sm font-black text-emerald-600">₹{pkg.price}</p>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 )}

//                 <div className="flex flex-col-reverse sm:flex-row gap-3 pt-8">
//                   {activeStep > 0 && (
//                     <button
//                       type="button"
//                       onClick={() => setActiveStep(s => s - 1)}
//                       className="w-full py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600"
//                     >
//                       Back
//                     </button>
//                   )}
//                   <button
//                     type="submit"
//                     className="w-full flex items-center justify-center gap-2 rounded-xl py-4 text-[10px] font-black uppercase tracking-widest text-white transition-all active:scale-95"
//                     style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)" }}
//                   >
//                     {activeStep === 0 ? "Proceed" : activeStep === steps.length - 1 ? "Complete" : "Next"}
//                     <ChevronRight size={14} />
//                   </button>
//                 </div>
//               </Form>
//             )}
//           </Formik>
//         </GlassCard>
//       </div>
//     </AuthLayout>
//   );
// }
